import { useState } from "react";
import { IconButton, Modal } from "src/Components";
import { Input } from "src/Components/Controls";
import { SelectBox } from "src/UI";
import { useEQSelector } from "src/Hooks";
import { IModalProps } from "..";
import { DenyReasonType } from "src/Types/ReDoc/Enter";
import { EQueueType } from "src/Types/ReDoc/EQueue";
import { Headline, RadioBtn } from "src/UI";
import "./CompleteModal.scss";

interface ICompleteModalProps extends IModalProps {};

const CompleteModal: React.FC<ICompleteModalProps> = ( { type, isOpen, onAction, onClose } ) => {

	const reasonList = useEQSelector<Array<DenyReasonType>>( type, "denyReasons" ) || [];
	const [ result, setResult ] = useState<string>( RESULT_SUCCESS );
	const [ reason, setReason ] = useState<string>( "" );
	const [ otherReason, setOtherReason ] = useState<string>( "" );
	const [ comment, setComment ] = useState<string>( "" );
	const isReturn = type === EQueueType.Return;
	const isDeniedResult = result === RESULT_DENY;
	const isOtherReason = reason === OTHER_REASON;
	const selectedReasonItem = defaultDenyReasons.find( item => typeof item === "object" && (item as { id: unknown }).id === reason );
	const selectedReasonTitle = (typeof selectedReasonItem === "object" && selectedReasonItem?.title) ? selectedReasonItem.title : "";
	const resolvedReason = isOtherReason ? otherReason.trim() : selectedReasonTitle;
	const reasonOptions = isReturn ? defaultDenyReasons : toOptions(reasonList);

	const onActionHandler = () => {
		if ( isReturn ) {
			if ( isDeniedResult && !resolvedReason ) return;
			onAction( "complete", { result, reason: resolvedReason, comment } );
			onClose();
			return;
		}
		if ( isDeniedResult ) {
			if ( !reason ) return;
			onAction( "reasons", reason );
		}
		else {
			onAction( "complete" );
		}
		onClose();
	};

	const handleResultChange = ( value: string ) => {
		setResult( value );
		if ( value !== RESULT_DENY ) {
			setReason( "" );
			setOtherReason( "" );
		}
	};

	const handleReasonChange = ( value: string ) => {
		setReason( value );
		if ( value !== OTHER_REASON ) setOtherReason( "" );
	};

	const renderStandardBody: React.FC = () => (
		<div className="modal_wrapper modal_wrapper-redirect">
			<Headline>Результат оказания услуги</Headline>
			<SelectBox
				options={results}
				selectedValues={result ? [result] : []}
				onChangeSelected={(items) => handleResultChange(items[0]?.id ?? "")}
				placeholder="Выберите результат оказания услуги..."
			/>
			{
				isDeniedResult &&
				<>
					<Headline>Причина неоказания услуги</Headline>
					<SelectBox
						options={reasonOptions}
						selectedValues={reason ? [reason] : []}
						onChangeSelected={(items) => handleReasonChange(items[0]?.id ?? "")}
						placeholder="Выберите причину неоказания услуги..."
						allowClear
					/>
				</>
			}
		</div>
	);

	const renderReturnBody: React.FC = () => (
		<div className="return-complete-modal__body">
			<label className="return-complete-modal__label">
				Результат оказания услуги <span>*</span>
			</label>
			<div className="return-complete-modal__results">
				{results.map(item => (
					<RadioBtn
						key={item.id}
						name="return-service-result"
						value={item.id}
						label={item.title || ""}
						checked={result === item.id}
						onChange={() => handleResultChange(String(item.id))}
					/>
				))}
			</div>
			{isDeniedResult && (
				<>
					<label className="return-complete-modal__label">
						Причина неоказания услуги <span>*</span>
					</label>
					<SelectBox
						options={reasonOptions}
						selectedValues={reason ? [reason] : []}
						onChangeSelected={(items) => handleReasonChange(items[0]?.id ?? "")}
						placeholder="Выберите причину из списка"
						allowClear
					/>
					{isOtherReason && (
						<>
							<label className="return-complete-modal__label">
								Другая причина <span>*</span>
							</label>
							<Input
								value={otherReason}
								onChangeValue={setOtherReason}
								className="return-complete-modal__other-reason"
							/>
						</>
					)}
				</>
			)}
			<label className="return-complete-modal__label">Комментарий к посетителю</label>
			<Input
				type="textarea"
				value={comment}
				onChangeValue={setComment}
				className="return-complete-modal__comment"
			/>
		</div>
	);

	const renderModalToolbar = () => {
		const isDisabled = isDeniedResult && !resolvedReason;
		return (
			<>
				<IconButton color={isReturn ? "red" : "blue"} outline onClick={onClose}>Отмена</IconButton>
				<IconButton color={isReturn ? "green" : "blue"} disable={isDisabled} onClick={onActionHandler}>
					{isReturn ? "Завершить работу" : "Завершить прием"}
				</IconButton>
			</>
		)
	};

	return (
		<Modal
			isOpen={isOpen}
			title={isReturn ? "Завершение приёма" : "Завершение работы с посетителем"}
			titleType={isReturn ? "small" : "large"}
			onClose={onClose}
			color="light"
			className={isReturn ? "return-complete-modal" : "not_scrolled"}
			renderFooter={renderModalToolbar()}
			renderBody={isReturn ? renderReturnBody : renderStandardBody}
		/>
	);
};

const RESULT_SUCCESS = "0";
const RESULT_DENY = "1";
const OTHER_REASON = "other";

const results: Array<{ id: string; title: string }> = [
	{
		id: RESULT_SUCCESS,
		title: "Услуга оказана"
	},
	{
		id: RESULT_DENY,
		title: "Услуга не оказана"
	}
];

const defaultDenyReasons: Array<{ id: string; title: string }> = [
	{
		id: "visitor-refused-service",
		title: "Посетитель отказался от получения услуги"
	},
	{
		id: "missing-documents",
		title: "Отсутствуют необходимые документы"
	},
	{
		id: "incorrect-request",
		title: "Обращение не относится к услугам окна"
	},
	{
		id: "identity-not-confirmed",
		title: "Не удалось подтвердить данные посетителя"
	},
	{
		id: "technical-issue",
		title: "Техническая невозможность оказания услуги"
	},
	{
		id: "other",
		title: "Другая причина"
	}
];

const toOptions = ( items: Array<DenyReasonType> ): Array<{ id: string; title: string }> =>
	items.map( (item: DenyReasonType) => ({
		id: item.id,
		title: item.name
	}));

export default CompleteModal;
